/**
 * Arrow navigation for `.carousel-content` slideshows (the multi-column card
 * carousels).
 *
 * Horizon's Slideshow pages by `current + visibleSlides.length` and keeps
 * `current` in slide-index terms, which desynchronizes from the real scroll
 * position whenever the row overflows by less than one full page of slides:
 * the arrow reads enabled, the click scrolls nowhere, and the carousel looks
 * broken — or the reverse, a normal-looking arrow that is silently disabled.
 * Which one you get depends on the viewport width, so it reads as "works the
 * first time, not the second".
 *
 * These carousels navigate by actual scroll distance instead, and the arrows'
 * disabled state follows the scroll position. Dots, dragging and the
 * component's own slide bookkeeping are untouched: scrolling still feeds the
 * component's scroll handler, which keeps `current` and the dots in sync.
 */

const SCOPE = '.carousel-content';

/** @param {Element} slideshow */
function scrollerOf(slideshow) {
  return slideshow.querySelector('slideshow-slides');
}

/**
 * Distance from one slide to the next, including the gap.
 * @param {HTMLElement} scroller
 */
function stepSize(scroller) {
  // Not .children: the scroller interleaves <style> tags between the slides.
  const slides = scroller.querySelectorAll(':scope > .card');
  if (slides.length > 1) {
    const first = /** @type {HTMLElement} */ (slides[0]);
    const second = /** @type {HTMLElement} */ (slides[1]);
    const step = second.offsetLeft - first.offsetLeft;
    if (step > 0) return step;
  }
  return /** @type {HTMLElement} */ (slides[0])?.offsetWidth || scroller.clientWidth;
}

/** @param {Element} slideshow */
function updateArrows(slideshow) {
  const scroller = scrollerOf(slideshow);
  if (!scroller) return;

  const previous = slideshow.querySelector('.slideshow-control--previous');
  const next = slideshow.querySelector('.slideshow-control--next');
  const max = scroller.scrollWidth - scroller.clientWidth;
  const position = Math.abs(scroller.scrollLeft);

  if (previous instanceof HTMLButtonElement) previous.disabled = position <= 1;
  if (next instanceof HTMLButtonElement) next.disabled = position >= max - 1;
}

/**
 * Page-based dots. The native dots are one-per-SLIDE while the arrows move a
 * full page (3 slides on desktop), so eight cards showed eight dots and the
 * active dot crawled a third of the row per click (client QA 28 Aug). The
 * native list is hidden and replaced with one dot per PAGE; the page count
 * follows the real geometry, so mobile (1 per view) still gets one per card.
 * @param {Element} slideshow
 */
function buildPageDots(slideshow) {
  const scroller = scrollerOf(slideshow);
  const nativeDots = slideshow.querySelector('ol.slideshow-controls__dots');
  if (!scroller || !nativeDots) return;

  const step = stepSize(scroller);
  const perPage = Math.max(1, Math.floor(scroller.clientWidth / step));
  const total = scroller.querySelectorAll(':scope > .card').length;
  const pages = Math.max(1, Math.ceil(total / perPage));

  let ours = slideshow.querySelector('ol[data-page-dots]');
  if (ours && Number(ours.dataset.pages) === pages) return;
  if (ours) ours.remove();

  nativeDots.style.display = 'none';
  ours = document.createElement('ol');
  ours.className = nativeDots.className;
  ours.setAttribute('data-page-dots', '');
  ours.dataset.pages = String(pages);
  for (let i = 0; i < pages; i++) {
    const li = document.createElement('li');
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'slideshow-control button button-unstyled';
    b.setAttribute('data-page-dot', String(i));
    b.setAttribute('aria-label', 'Page ' + (i + 1) + ' of ' + pages);
    b.textContent = String(i + 1); // native dots carry their index too
    if (i === 0) b.setAttribute('aria-current', 'true');
    li.appendChild(b);
    ours.appendChild(li);
  }
  nativeDots.after(ours);
  syncPageDots(slideshow);
}

/** @param {Element} slideshow */
function syncPageDots(slideshow) {
  const scroller = scrollerOf(slideshow);
  const ours = slideshow.querySelector('ol[data-page-dots]');
  if (!scroller || !ours) return;
  const step = stepSize(scroller);
  const perPage = Math.max(1, Math.floor(scroller.clientWidth / step));
  const pages = Number(ours.dataset.pages);
  const max = scroller.scrollWidth - scroller.clientWidth;
  // The clamped last page makes position/pageWidth undershoot; snap to the
  // last dot once the scroller is at (or within a slide of) the end.
  let page = Math.round(Math.abs(scroller.scrollLeft) / (step * perPage));
  if (Math.abs(scroller.scrollLeft) >= max - 1) page = pages - 1;
  page = Math.min(page, pages - 1);
  ours.querySelectorAll('button').forEach((b, i) => {
    if (i === page) b.setAttribute('aria-current', 'true');
    else b.removeAttribute('aria-current');
  });
}

/** @param {Element} slideshow */
function watch(slideshow) {
  if (slideshow.hasAttribute('data-arrow-nav')) return;
  slideshow.setAttribute('data-arrow-nav', '');

  const scroller = scrollerOf(slideshow);
  if (!scroller) return;

  let frame = 0;
  // rAF so this lands after the component's own scroll handler, which rewrites
  // the disabled states from its index arithmetic on every scroll event.
  scroller.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        updateArrows(slideshow);
        syncPageDots(slideshow);
      });
    },
    { passive: true }
  );

  new ResizeObserver(() => {
    updateArrows(slideshow);
    buildPageDots(slideshow);
    syncPageDots(slideshow);
  }).observe(scroller);
  updateArrows(slideshow);
  buildPageDots(slideshow);
}

// Capture phase, so the component's own /next-/previous handler never runs
// for these arrows.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    // Page dots: jump straight to that page's first slide.
    const dot = event.target.closest(`${SCOPE} [data-page-dot]`);
    if (dot instanceof HTMLButtonElement) {
      const slideshow = dot.closest('slideshow-component');
      const scroller = slideshow && scrollerOf(slideshow);
      if (!scroller) return;
      event.stopPropagation();
      event.preventDefault();
      const step = stepSize(scroller);
      const perPage = Math.max(1, Math.floor(scroller.clientWidth / step));
      scroller.scrollTo({ left: Number(dot.dataset.pageDot) * step * perPage, behavior: 'smooth' });
      return;
    }

    // Arrows ONLY — the old `.slideshow-control` match also swallowed clicks
    // on the (now hidden) native dot buttons, which share that class, and
    // paged forward instead of selecting.
    const control = event.target.closest(
      `${SCOPE} .slideshow-control--previous, ${SCOPE} .slideshow-control--next`
    );
    if (!(control instanceof HTMLButtonElement) || control.disabled) return;

    const slideshow = control.closest('slideshow-component');
    const scroller = slideshow && scrollerOf(slideshow);
    if (!scroller) return;

    event.stopPropagation();
    event.preventDefault();

    const direction = control.classList.contains('slideshow-control--previous') ? -1 : 1;
    const step = stepSize(scroller);
    const perPage = Math.max(1, Math.floor(scroller.clientWidth / step));

    // scrollBy clamps at the edges, so a partial last page scrolls exactly the
    // remaining distance instead of dying like the index jump did.
    scroller.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
  },
  true
);

function arm() {
  document.querySelectorAll(`slideshow-component${SCOPE}`).forEach(watch);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', arm);
} else {
  arm();
}

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', arm);
